import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Helper to get CSRF token from cookie
const getCsrfToken = () => {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

// Helper to check if user is authenticated via cookie
export const isAuthenticatedViaCookie = () => {
  // CSRF token cookie indicates auth token cookie is set (both are set together)
  return document.cookie.includes('csrf_token=');
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  withCredentials: true, // Critical: send cookies with requests
});

// Add CSRF token to requests for state-changing operations
api.interceptors.request.use((config) => {
  // Add CSRF token header for state-changing requests
  if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired, redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  uploadProfilePicture: (formData) => {
    return api.post('/user/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Content API
export const contentAPI = {
  // Upload single file
  uploadFile: (formData, onProgress) => {
    return api.post('/upload/single', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  // Chunked upload
  startChunkedUpload: (data) => api.post('/upload/chunked/start', data),
  uploadChunk: (uploadId, chunkNumber, formData) => {
    return api.post(`/upload/chunked/${uploadId}/chunk/${chunkNumber}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  completeChunkedUpload: (uploadId, data) => {
    return api.post(`/upload/chunked/${uploadId}/complete`, data);
  },

  // Content management
  getPublicContent: (params = {}) => api.get('/content', { params }),
  getUserContent: (params = {}) => api.get('/user/content', { params }),
  getContentDetails: (contentId) => api.get(`/content/${contentId}`),
  updateContent: (contentId, data) => api.put(`/content/${contentId}`, data),
  deleteContent: (contentId) => api.delete(`/content/${contentId}`),
};

// Messaging API
export const messagingAPI = {
  getConversations: () => api.get('/conversations'),
  createConversation: (data) => api.post('/conversations', data),
  getMessages: (conversationId, params = {}) => {
    return api.get(`/conversations/${conversationId}/messages`, { params });
  },
  sendMessage: (data) => api.post('/messages', data),
};

// Analytics API
export const analyticsAPI = {
  getContentAnalytics: (contentId, days = 30) => {
    return api.get(`/content/${contentId}/analytics`, { params: { days } });
  },
  getUserEngagement: () => api.get('/user/engagement'),
  getAnalyticsDashboard: (days = 30) => {
    return api.get('/analytics/dashboard', { params: { days } });
  },
};

// Streaming API
export const streamingAPI = {
  getMasterPlaylist: (contentId) => {
    return `${BACKEND_URL}/api/stream/${contentId}/master.m3u8`;
  },
  getStreamSegment: (contentId, filename) => {
    return `${BACKEND_URL}/api/stream/${contentId}/${filename}`;
  },
};

// Admin API
export const adminAPI = {
  getPendingContent: (params = {}) => api.get('/admin/content/pending', { params }),
  approveContent: (contentId) => api.post(`/admin/content/${contentId}/approve`),
  rejectContent: (contentId, reason) => {
    return api.post(`/admin/content/${contentId}/reject`, { reason });
  },
};

// File serving - No longer pass token in URL (security risk)
// Files are now accessed via cookies or public URLs
export const getFileURL = (filePath) => {
  // For authenticated file access, cookies will be sent automatically
  // due to withCredentials: true on the axios instance
  return `${BACKEND_URL}/api/files/${filePath}`;
};

// Chunk upload utility
export class ChunkedUploader {
  constructor(file, options = {}) {
    this.file = file;
    this.chunkSize = options.chunkSize || 1024 * 1024; // 1MB default
    this.maxRetries = options.maxRetries || 3;
    this.onProgress = options.onProgress || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    
    this.uploadId = null;
    this.totalChunks = Math.ceil(file.size / this.chunkSize);
    this.uploadedChunks = 0;
  }

  async start(metadata) {
    try {
      // Start upload session
      const startData = {
        filename: this.file.name,
        file_size: this.file.size,
        total_chunks: this.totalChunks,
      };

      const startResponse = await contentAPI.startChunkedUpload(startData);
      this.uploadId = startResponse.data.upload_id;

      // Upload chunks
      for (let chunkNumber = 0; chunkNumber < this.totalChunks; chunkNumber++) {
        await this.uploadChunk(chunkNumber);
      }

      // Complete upload
      const completeResponse = await contentAPI.completeChunkedUpload(
        this.uploadId,
        metadata
      );

      this.onComplete(completeResponse.data);
      return completeResponse.data;

    } catch (error) {
      this.onError(error);
      throw error;
    }
  }

  async uploadChunk(chunkNumber, retryCount = 0) {
    try {
      const start = chunkNumber * this.chunkSize;
      const end = Math.min(start + this.chunkSize, this.file.size);
      const chunk = this.file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk);

      await contentAPI.uploadChunk(this.uploadId, chunkNumber, formData);

      this.uploadedChunks++;
      const progress = (this.uploadedChunks / this.totalChunks) * 100;
      this.onProgress(progress, chunkNumber, this.totalChunks);

    } catch (error) {
      if (retryCount < this.maxRetries) {
        console.warn(`Retrying chunk ${chunkNumber}, attempt ${retryCount + 1}`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return this.uploadChunk(chunkNumber, retryCount + 1);
      }
      throw error;
    }
  }
}

export default api;
